// Function index:
//   renderLlmModel(host) — fetch the current admin model override + the curated
//       quick-pick shortlist + the OpenRouter catalog; render the quick-picks
//       (one click switches the live GM model) above a filterable full-catalog
//       picker with Save / Clear. Clearing reverts to automatic (tier) select.

import {
  clearAdminLlmModel, getAdminLlmCatalog, getAdminLlmModel,
  setAdminLlmModel, type AdminLlmCatalogModel,
} from '../../api.js';
import { esc } from './helpers.js';

export async function renderLlmModel(host: HTMLElement): Promise<void> {
  async function load(): Promise<void> {
    host.innerHTML = 'Loading…';
    try {
      const [{ model, curated }, { models }] = await Promise.all([getAdminLlmModel(), getAdminLlmCatalog()]);
      host.innerHTML = render(model, curated ?? [], models.length);
      wireQuickPicks();
      wire(model, models);
    } catch (err) {
      host.innerHTML = '';
      const e = document.createElement('div');
      e.className = 'party-meta';
      e.textContent = `Could not load LLM model settings: ${(err as Error).message}`;
      host.appendChild(e);
    }
  }

  // One-click switching between the curated GM models. The active model's
  // button is disabled (already selected); clicking any other sets the live
  // override and reloads.
  function wireQuickPicks(): void {
    const picks = host.querySelectorAll<HTMLButtonElement>('.lm-pick');
    picks.forEach((btn) => {
      btn.addEventListener('click', async () => {
        if (btn.disabled) return;
        picks.forEach((b) => { b.disabled = true; });
        try { await setAdminLlmModel(btn.dataset.id!); await load(); }
        catch (err) { alert(`switch failed: ${(err as Error).message}`); await load(); }
      });
    });
  }

  function wire(current: string | null, models: AdminLlmCatalogModel[]): void {
    const filter = host.querySelector<HTMLInputElement>('.lm-filter')!;
    const list = host.querySelector<HTMLSelectElement>('.lm-list')!;
    const save = host.querySelector<HTMLButtonElement>('.lm-save')!;
    const clear = host.querySelector<HTMLButtonElement>('.lm-clear');

    const fill = (q: string): void => {
      const needle = q.trim().toLowerCase();
      const subset = needle
        ? models.filter((mo) => mo.id.toLowerCase().includes(needle) || mo.name.toLowerCase().includes(needle))
        : models;
      list.innerHTML = subset.slice(0, 200).map((mo, i) =>
        `<option value="${esc(mo.id)}"${mo.id === current || (i === 0 && !current) ? ' selected' : ''}>${esc(mo.name)} — ${esc(mo.id)}</option>`,
      ).join('');
      save.disabled = list.value === '';
    };
    fill('');

    filter.addEventListener('input', () => fill(filter.value));
    list.addEventListener('change', () => { save.disabled = list.value === ''; });
    save.addEventListener('click', async () => {
      save.disabled = true;
      try { await setAdminLlmModel(list.value); await load(); }
      catch (err) { save.disabled = false; alert(`save failed: ${(err as Error).message}`); }
    });
    clear?.addEventListener('click', async () => {
      clear.disabled = true;
      try { await clearAdminLlmModel(); await load(); }
      catch (err) { clear.disabled = false; alert(`clear failed: ${(err as Error).message}`); }
    });
  }

  function render(current: string | null, curated: Array<{ id: string; label: string }>, catalogSize: number): string {
    const status = current
      ? `Override active: <strong>${esc(current)}</strong> <button type="button" class="secondary lm-clear">Clear override</button>`
      : 'No override — automatic (subscription-tier) model selection.';

    const picks = curated.length
      ? `<div class="party-meta" style="margin:6px 0 4px;">Quick picks</div>
         <div style="display:flex; flex-wrap:wrap; gap:6px; margin-bottom:14px;">
           ${curated.map((m) => {
             const active = m.id === current;
             const style = active
               ? 'border-color:var(--color-accent); color:var(--color-accent); font-weight:600;'
               : '';
             return `<button type="button" class="secondary lm-pick${active ? ' active' : ''}" data-id="${esc(m.id)}"`
               + `${active ? ' disabled' : ''} title="${esc(m.id)}" style="${style}">`
               + `${esc(m.label)}${active ? ' ✓' : ''}</button>`;
           }).join('')}
         </div>`
      : '';

    return `
      <div class="party-meta" style="margin-bottom:10px;">${status}</div>
      ${picks}
      <div style="max-width:560px; display:flex; flex-direction:column; gap:8px;">
        <div class="party-meta">Or pick any model from the full catalog:</div>
        <input class="lm-filter" type="text" placeholder="Filter ${catalogSize} models…" />
        <select class="lm-list" size="12" style="width:100%;"></select>
        <div><button type="button" class="lm-save">Use selected model</button></div>
      </div>`;
  }

  await load();
}
